import React, { lazy, Suspense, useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import fileStyles from './FilePanel.module.css';
import MermaidRenderer from './MermaidRenderer';
import UnifiedContentRenderer from './components/core/UnifiedContentRenderer';
import { processContent } from './components/core/ContentUtils';
import EnhancedMDXEditor from './components/enhanced/EnhancedMDXEditor';
// 注意：不再使用 react-drawio，改为直接使用 iframe 嵌入 draw.io
// 使用 React.lazy 动态导入组件
const EnhancedJsonRenderer = lazy(() => import('./format-json-content'));
// 导入UISandboxRenderer组件
const UISandboxRenderer = lazy(() => import('./components/UISandboxRenderer'));
// 导入ExcelSandboxRenderer组件
const ExcelSandboxRenderer = lazy(() => import('./components/ExcelSandboxRenderer'));

// 添加全局缓存来存储已渲染的内容
const renderedContentCache = new Map();

// 全局首次进入标志映射
const fileFirstEntryMap = new Map();

// 全局调试开关 - 可根据需要修改
const ENABLE_MERMAID_DEBUG = false; // 设置为true启用调试信息

// ===== 新增：从App.js迁移的与Draft交互相关的函数 =====

/**
 * 用于存储当前选中的文件以及其行号
 * @type {Object}
 */
let selectedFileData = {
  fileName: null,
  lineIndices: []
};

/**
 * 用于存储已经添加的评论
 * @type {Object}
 */
let diffComments = {};

/**
 * 处理文件行的选择操作
 * @param {string} fileName - 文件名
 * @param {number} lineIndex - 行索引
 * @param {boolean} isSelected - 是否选中
 */
export const handleFileLineSelection = (fileName, lineIndex, isSelected) => {
  // 初始化当前文件的选择行
  if (!selectedFileData.lineIndices) {
    selectedFileData.lineIndices = [];
  }

  // 更新选中的文件名
  selectedFileData.fileName = fileName;

  // 更新选中的行
  if (isSelected) {
    // 如果行还未被选中，添加到选中列表
    if (!selectedFileData.lineIndices.includes(lineIndex)) {
      selectedFileData.lineIndices.push(lineIndex);
    }
  } else {
    // 如果行已被选中，从选中列表中移除
    selectedFileData.lineIndices = selectedFileData.lineIndices.filter(index => index !== lineIndex);
  }
  
  console.log(`File ${fileName}, Line ${lineIndex}, Selected: ${isSelected}`);
  console.log('Current selection:', selectedFileData);
};

/**
 * 处理差异行点击事件
 * @param {string} fileName - 文件名
 * @param {number} lineIndex - 行索引
 */
export const handleDiffLineClick = (fileName, lineIndex) => {
  // 初始化选中状态
  const isCurrentlySelected = selectedFileData.fileName === fileName && 
                             selectedFileData.lineIndices.includes(lineIndex);
  
  // 切换选中状态
  handleFileLineSelection(fileName, lineIndex, !isCurrentlySelected);
};

/**
 * 处理评论变更
 * @param {string} fileName - 文件名
 * @param {number} lineIndex - 行索引
 * @param {string} comment - 评论内容
 */
export const handleCommentChange = (fileName, lineIndex, comment) => {
  if (!diffComments[fileName]) {
    diffComments[fileName] = {};
  }
  diffComments[fileName][lineIndex] = comment;
  console.log(`Comment added for ${fileName}, Line ${lineIndex}:`, comment);
  console.log('Current comments:', diffComments);
};

/**
 * 发送选中内容到后端
 * @param {WebSocket} ws - WebSocket连接
 * @param {string} messageId - 消息ID
 * @param {Array} [selectedLines] - 可选，直接提供的选中行索引
 * @param {Object} [providedComments] - 可选，直接提供的评论
 */
export const sendSelectedContentToBackend = (ws, messageId, selectedLines, providedComments) => {
  // 确定使用哪个文件名和行索引
  const fileName = selectedFileData.fileName;
  const lineIndices = selectedLines || selectedFileData.lineIndices;
  
  // 如果没有选择任何内容，返回
  if ((!fileName && !selectedLines) || lineIndices.length === 0) {
    console.warn('No content selected to send');
    return;
  }

  // 记录实际使用的文件ID
  const actualFileId = messageId || fileName;
  console.log('发送数据到后端 - 使用的文件ID:', actualFileId);
  console.log('window._fileContents 键:', Object.keys(window._fileContents || {}));

  // 获取文件内容 - 尝试多种方式
  let fileContent = null;
  
  // 直接使用文件ID
  if (window._fileContents && window._fileContents[actualFileId]) {
    fileContent = window._fileContents[actualFileId];
  } 
  // 尝试使用简化的文件名
  else if (window._fileContents && fileName) {
    const simpleFileName = fileName.split('/').pop();
    if (window._fileContents[simpleFileName]) {
      fileContent = window._fileContents[simpleFileName];
      // 同步缓存，确保下次可以直接使用
      window._fileContents[actualFileId] = fileContent;
    }
  }
  
  if (!fileContent) {
    console.error('文件内容未找到:', actualFileId);
    return;
  }

  // 处理文件内容 - 检查是否是Draft文件（JSON数组格式）
  let parsedContent = fileContent;
  let lineContents = {};
  
  try {
    // 尝试解析为JSON，检查是否为Draft格式
    if (typeof fileContent === 'string') {
      parsedContent = JSON.parse(fileContent);
    }
    
    // 如果是数组且元素有type和content字段，则视为Draft格式
    if (Array.isArray(parsedContent) && parsedContent.length > 0 && 
        'type' in parsedContent[0] && 'content' in parsedContent[0]) {
      // 记录每行的内容
      lineIndices.forEach(lineIndex => {
        if (lineIndex < parsedContent.length) {
          lineContents[lineIndex] = parsedContent[lineIndex];
        }
      });
    }
  } catch (e) {
    // 非JSON格式，按普通文本处理
    console.log('Content is not in Draft JSON format, treating as plain text');
  }

  // 提取选中的行
  let selectedContent = '';
  let isPlainText = true;
  
  if (Object.keys(lineContents).length > 0) {
    // Draft格式，使用已获取的行内容
    isPlainText = false;
    Object.entries(lineContents).forEach(([lineIndex, lineData]) => {
      selectedContent += `${lineData.type}: ${lineData.content}\n`;
    });
  } else {
    // 普通文本格式，按行分割
    const lines = fileContent.split('\n');
    lineIndices.forEach(lineIndex => {
      if (lineIndex < lines.length) {
        selectedContent += lines[lineIndex] + '\n';
        // 记录行内容用于lineContents
        lineContents[lineIndex] = lines[lineIndex];
      }
    });
  }

  // 获取评论 - 使用提供的评论或从diffComments中获取
  const comments = providedComments || diffComments[fileName] || {};
  
  // 准备发送的数据
  const dataToSend = {
    type: 'user_selection',
    messageId: messageId,
    fileName: fileName,
    content: selectedContent.trim(),
    lineIndices: lineIndices,
    lineContents: lineContents, // 添加行内容
    comments: comments,
    format: isPlainText ? 'text' : 'draft' // 添加格式标识
  };

  // 发送数据
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(dataToSend));
    console.log('Selection sent to backend:', dataToSend);
    
    // 如果没有提供外部参数，则清除内部状态
    if (!selectedLines && !providedComments) {
      selectedFileData = { fileName: null, lineIndices: [] };
      diffComments = {};
    }
  } else {
    console.error('WebSocket not open');
  }
};

// 获取文件语言类型
export const getLanguage = (fileName) => {
  if (!fileName) return "text" // 默认返回'text'如果fileName不存在
  
  // 🔑 修复：.final 结尾的文件强制使用 markdown，避免YAML解析错误
  if (fileName.endsWith(".final")) {
    return "markdown"
  }
  
  const extension = fileName.split(".").pop().toLowerCase()
  const languageMap = {
    py: "python",
    js: "javascript",
    jsx: "javascript",
    java: "java",
    cs: "csharp",
    cpp: "cpp",
    c: "cpp",
    rb: "ruby",
    swift: "swift",
    go: "golang",
    kt: "kotlin",
    php: "php",
    sql: "sql",
    ts: "typescript",
    tsx: "typescript",
    html: "html",
    css: "css",
    json: "json",
    yaml: "yaml",
    yml: "yaml",
    xml: "xml",      
  }
  return languageMap[extension] || "text"
}

// 修改 splitContentWithTables 函数
export const splitContentWithTables = (content) => {
  const sections = [];
  const lines = content.split('\n');
  let currentSection = [];
  let isInTable = false;

  for (let line of lines) {
    const trimmedLine = line.trim();
    
    // 检测表格行
    if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
      if (!isInTable) {
        // 开始新表格
        if (currentSection.length > 0) {
          sections.push({
            type: 'code',
            content: currentSection.join('\n').trim()
          });
          currentSection = [];
        }
        isInTable = true;
      }
      currentSection.push(trimmedLine);
    } else {
      if (isInTable) {
        // 表格结束
        if (currentSection.length > 0) {
          sections.push({
            type: 'table',
            content: currentSection.join('\n').trim()
          });
          currentSection = [];
        }
        isInTable = false;
      }
      currentSection.push(line);
    }
  }

  // 处理最后一个部分
  if (currentSection.length > 0) {
    sections.push({
      type: isInTable ? 'table' : 'code',
      content: currentSection.join('\n').trim()
    });
  }

  return sections;
};

/**
 * Draft内容渲染组件
 * @param {object} props - 组件属性
 * @returns {JSX.Element} React组件
 */
const DraftContentRenderer = ({ fileName, content, options }) => {
  const { 
    selectedDiffLines = {}, 
    handleDiffLineClick = () => {},
    diffComments = {}, 
    handleCommentChange = () => {},
    ws = null,
    messageId = null
  } = options;
  
  // 将当前选中的文件数据与options同步
  useEffect(() => {
    if (selectedDiffLines[fileName]) {
      selectedFileData.fileName = fileName;
      selectedFileData.lineIndices = selectedDiffLines[fileName] || [];
    }
    
    if (diffComments[fileName]) {
      window.diffComments = diffComments;
    }
  }, [selectedDiffLines, diffComments, fileName]);

  // 处理行点击，扩展handleDiffLineClick以添加控制台输出
  const handleLineClick = (lineIndex) => {
    // 获取行内容
    let lineContent = "未找到内容";
    if (Array.isArray(content) && lineIndex < content.length) {
      const line = content[lineIndex];
      lineContent = `${line.type}: ${line.content}`;
      
      // 在控制台打印行号和内容
      console.log(`行号: ${lineIndex}, 内容: ${lineContent}`);
    }
    
    // 调用原始的handleDiffLineClick
    handleDiffLineClick(fileName, lineIndex);
  };
  
  return (
    <div className={fileStyles.draftContentContainer} data-file-name={fileName}>
      <Suspense fallback={<div>加载中...</div>}>
        <EnhancedJsonRenderer
          content={content}
          selectedLines={selectedDiffLines[fileName] || []}
          onLineClick={handleLineClick}
          comments={diffComments[fileName] || {}}
          onCommentChange={(lineIndex, comment) => 
            handleCommentChange(fileName, lineIndex, comment)}
        />
      </Suspense>
    </div>
  );
};

/**
 * 处理draft内容
 * @param {Array} draftContent draft内容数组
 * @returns {Array} 处理后的draft内容数组
 */
export const preprocessDraftContent = (draftContent) => {
  if (!draftContent || !Array.isArray(draftContent)) {
    return [];
  }

  // 定义支持的Mermaid图表类型标记
  const mermaidTypes = [
    "```mermaid", "graph ", "flowchart ", "sequenceDiagram", "classDiagram", 
    "mindmap", "quadrantChart", "erDiagram", "gantt", "pie", "stateDiagram", 
    "requirementDiagram", "journey", "timeline", "gitGraph", "sankey", "C4Context",
    "C4Container", "C4Component", "C4Dynamic", "C4Deployment"
  ];

  // 检测内容是否包含Mermaid图表
  const containsMermaid = (content) => {
    return mermaidTypes.some(type => content.includes(type));
  };

  // 深拷贝以避免修改原始数据
  const processedContent = JSON.parse(JSON.stringify(draftContent));
  
  // 处理特殊字段，如mermaid图表
  processedContent.forEach(item => {
    if (item.type === 'added' && item.content) {
      // 特殊处理Mermaid图表内容
      if (containsMermaid(item.content)) {
        // 处理多重转义的情况
        if (item.content.includes('\\\\n') || item.content.includes('\\\\\"') || item.content.includes('\\\\u')) {
          try {
            // 替换常见的转义序列
            item.content = item.content
              .replace(/\\\\n/g, '\n')
              .replace(/\\\\\"/g, '"')
              .replace(/\\\\u003e/g, '>')
              .replace(/\\\\u003c/g, '<')
              .replace(/\\\\/g, '\\');
          } catch (e) {
            console.error('处理draft中的Mermaid内容时出错:', e);
          }
        }
      }
      
      // 如果内容是竞争四象限图的特殊处理
      if (item.content.includes('竞争四象限图') && item.content.includes('```mermaid')) {
        try {
          // 尝试提取和处理JSON格式的Mermaid内容
          const match = item.content.match(/"竞争四象限图"\s*:\s*"(.+?)"/s);
          if (match && match[1]) {
            let chartContent = match[1];
            // 替换转义序列
            chartContent = chartContent
              .replace(/\\\\n/g, '\n')
              .replace(/\\\\\"/g, '"')
              .replace(/\\\\u003e/g, '>')
              .replace(/\\\\u003c/g, '<')
              .replace(/\\\\/g, '\\');
            
            // 更新原始内容
            item.content = item.content.replace(match[1], chartContent);
          }
        } catch (e) {
          console.error('处理竞争四象限图时出错:', e);
        }
      }
      
      // 通用的处理任何在JSON字符串中的Mermaid图表
      if (item.content.includes('"```mermaid') || item.content.match(/"\s*graph /) || 
          item.content.includes('"mindmap') || item.content.includes('"quadrantChart')) {
        try {
          // 寻找可能嵌入在JSON中的Mermaid图表
          const embedRegex = /"(```mermaid[\s\S]*?```|graph [\s\S]*?;|mindmap[\s\S]*?;|quadrantChart[\s\S]*?;)"/g;
          let match;
          while ((match = embedRegex.exec(item.content)) !== null) {
            if (match[1] && match[1].includes('\\n')) {
              const fixedContent = match[1].replace(/\\n/g, '\n');
              item.content = item.content.replace(match[1], fixedContent);
            }
          }
        } catch (e) {
          console.error('处理嵌入JSON的Mermaid图表时出错:', e);
        }
      }
    }
  });
  
  return processedContent;
};

/**
 * 文件内容渲染入口函数 - 作为调度器将内容分发到专门的渲染器
 * 
 * 这个函数作为主入口点，执行以下步骤：
 * 1. 标准化渲染选项
 * 2. 处理对象格式的内容（包含content和language字段）
 * 3. 确保内容是字符串类型
 * 4. 调用detectContentType检测内容类型
 * 5. 根据内容类型分发到专门的渲染器函数
 * 
 * 内容类型包括：
 * - markdown-table: 包含表格的Markdown
 * - markdown: 普通Markdown
 * - mermaid: 包含Mermaid图表
 * - text: 纯文本
 * - json: JSON格式
 * - draft-diff: Draft差异数据
 * - code: 需要语法高亮的代码(默认)
 * - final-markdown: .final文件中的Markdown内容
 * 
 * @param {string} fileName - 文件名，用于确定语言类型和缓存
 * @param {object|string} content - 文件内容，可以是字符串或对象({content, language})
 * @param {object} options - 渲染选项
 * @param {object} options.selectedFileLines - 已选择的文件行
 * @param {function} options.handleFileLineSelection - 处理文件行选择的函数
 * @param {boolean} options.fileContentExpanded - 文件内容是否展开
 * @param {object} options.selectedDiffLines - 已选择的差异行
 * @param {function} options.handleDiffLineClick - 处理差异行点击的函数
 * @param {object} options.diffComments - 差异行评论
 * @param {function} options.handleCommentChange - 处理评论变更的函数
 * @param {WebSocket} options.ws - WebSocket连接对象
 * @param {string} options.messageId - 消息ID，用于发送选择内容时标识
 * @param {string} options.senderInfo - 发送人信息（智能体名称）
 * @returns {JSX.Element} 渲染后的React组件
 */
export const renderFileContent = (fileName, content, {
  selectedFileLines = {}, 
  handleFileLineSelection: customHandleFileLineSelection = null, 
  fileContentExpanded = false,
  selectedDiffLines = {},
  handleDiffLineClick: customHandleDiffLineClick = null,
  diffComments: customDiffComments = {},
  handleCommentChange: customHandleCommentChange = null,
  ws = null,
  messageId = null,
  senderInfo = null,
  onContentChange = null,    // 添加缺失的参数
  fileId = null,            // 添加缺失的参数
  originalContent = null,   // A变量：原始内容
  currentPhase = '',        // 🔑 新增：当前项目阶段
  documentPhase = '',       // 🔑 新增：文档所属阶段
  addLocalMessage = null,   // 🔑 新增：本地消息添加函数
  sendSelectedContentToBackend = null,  // 可能还有其他缺失的参数
  messages = [],            // 🎨 绘图对话历史
  readOnly = false          // 是否只读（Claw 等纯展示场景传 true，链接可直接点击）
}) => {

  
  // 使用提供的处理函数或默认函数
  const actualHandleFileLineSelection = customHandleFileLineSelection || handleFileLineSelection;
  const actualHandleDiffLineClick = customHandleDiffLineClick || handleDiffLineClick;
  const actualHandleCommentChange = customHandleCommentChange || handleCommentChange;
  
  // 准备渲染选项
  const options = {
    selectedFileLines,
    handleFileLineSelection: actualHandleFileLineSelection,
    fileContentExpanded,
    selectedDiffLines,
    handleDiffLineClick: actualHandleDiffLineClick,
    diffComments: customDiffComments,
    handleCommentChange: actualHandleCommentChange,
    ws,
    messageId,
    onContentChange,          // 传递onContentChange
    fileId,                  // 传递fileId
    currentPhase,            // 🔑 传递当前阶段
    documentPhase,           // 🔑 传递文档阶段
    addLocalMessage,         // 🔑 传递本地消息添加函数
    sendSelectedContentToBackend,
    messages                 // 🎨 传递绘图对话历史
  };

  // 确保内容是正确的格式，对于draft文件也进行特殊处理
  let contentToRender = content;
  let contentType = detectContentType(fileName, content, senderInfo);
  
  // 如果是draft-diff类型并且内容是字符串，尝试解析为JSON
  if (contentType === 'draft-diff' && typeof content === 'string') {
    try {
      contentToRender = JSON.parse(content);
      // 添加预处理步骤：处理draft内容中的图表等
      if (Array.isArray(contentToRender)) {
        contentToRender = preprocessDraftContent(contentToRender);
      }
    } catch (e) {
      console.error('Failed to parse draft content:', e);
    }
  } else if (contentType === 'draft-diff' && Array.isArray(content)) {
    // 如果已经是数组格式但还没有处理过
    contentToRender = preprocessDraftContent(content);
  }
  
  // 检查内容是否为对象且包含 content 和 language 字段的格式
  if (content && typeof content === 'object' && 'content' in content && contentType !== 'draft-diff') {
    const fileContent = content.content;
    const language = content.language || getLanguage(fileName);
    
    // 确保 fileContent 是字符串
    const contentStr = typeof fileContent === 'string' ? fileContent : String(fileContent || '');
    
    // 缓存文件内容，以便后续使用
    if (!window._fileContents) {
      window._fileContents = {};
    }
    window._fileContents[fileName] = contentStr;
    
    // 检测内容类型并调用对应的渲染器
    contentType = detectContentType(fileName, contentStr, senderInfo);
    contentToRender = contentStr;
  } else if (typeof content === 'string' && contentType !== 'draft-diff') {
    // 如果内容是字符串类型（非draft-diff）
    if (!window._fileContents) {
      window._fileContents = {};
    }
    window._fileContents[fileName] = content;
    contentToRender = content;
  }
  
  // 根据内容类型分发到专门的渲染器
  switch (contentType) {
    case 'markdown-table':
      return renderMarkdownWithTables(fileName, contentToRender, options);
    case 'markdown':
      return renderPureMarkdown(fileName, contentToRender, options);
    case 'mermaid':
      return renderMermaidContent(fileName, contentToRender, options);
    case 'text':
      return renderPlainTextWithLines(fileName, contentToRender, options);
    case 'json':
      return renderJsonContent(fileName, contentToRender, options);
    case 'draft-diff':
      return <DraftContentRenderer 
               fileName={fileName} 
               content={contentToRender} 
               options={options} 
             />;
    case 'ui-sandbox':
      return (
        <Suspense fallback={<div>加载中...</div>}>
          <UISandboxRenderer 
            fileName={fileName} 
            content={contentToRender} 
            options={options} 
          />
        </Suspense>
      );
    case 'excel-sandbox':
      return (
        <Suspense fallback={<div>加载Excel计算器...</div>}>
          <ExcelSandboxRenderer 
            fileName={fileName} 
            content={contentToRender} 
            options={options} 
          />
        </Suspense>
      );
    case 'drawio':
      return renderDrawioContent(fileName, contentToRender, options);
    case 'final-markdown':
      // 调试信息：确认内容传递
      //console.log('FileContentRender: 渲染final-markdown', { 
      //  fileName, 
      //  contentLength: contentToRender?.length,
      //  contentPreview: contentToRender?.substring(0, 100) + '...'
      //});
      
      return (
        <EnhancedMDXEditor
          content={contentToRender}                    // B变量：当前编辑内容
          originalContent={originalContent || (typeof content === 'string' ? content : (content && typeof content === 'object' && 'content' in content) ? content.content : contentToRender)}  // A变量：尽量使用未经处理的原始内容
          readOnly={readOnly}  // 由调用方决定是否只读
          enableHistory={true}
          enableLLMCommands={true}
          enableThemeSwitch={true}  // 启用主题切换
          theme="dark"  // 默认深色主题
          fileId={options?.fileId || fileName}  // 传递文件ID
          currentPhase={options?.currentPhase || currentPhase}              // 🔑 传递当前阶段
          documentPhase={options?.documentPhase || documentPhase}            // 🔑 传递文档阶段
          ws={options?.ws || ws}                                  // 🔑 传递WebSocket连接
          addLocalMessage={options?.addLocalMessage}             // 🔑 传递本地消息添加函数
          onContentChange={(fileId, newContent, operation) => {
            const currentFileId = fileId || fileName;
            
            // 检查是否为首次进入且需要标准化
            if (!fileFirstEntryMap.get(currentFileId) && operation === 'manual') {
              console.log('检测到首次进入文件，执行A变量标准化:', currentFileId);
              
              // 标记该文件已完成首次进入
              fileFirstEntryMap.set(currentFileId, true);
              
              // 发送首次标准化请求，将MDXEditor处理后的内容同步到A变量
              if (options?.onContentChange) {
                options.onContentChange(currentFileId, newContent, 'first-entry-standardize');
              }
            } else {
              // 正常编辑操作，只更新B变量
              if (options?.onContentChange) {
                options.onContentChange(fileId, newContent, operation);
              }
            }
          }}
          onHistoryChange={(historyStack) => {
            // 更新历史状态
            console.log('编辑历史已更新:', { fileName, historyCount: historyStack.length });
            
            // 如果有历史更新回调，调用它
            if (options?.onHistoryChange) {
              options.onHistoryChange(fileName, historyStack);
            }
          }}
          style={{
            width: '100%'
          }}
        />
      );
    default:
      return renderCodeHighlight(fileName, contentToRender, options);
  }
};

// ===== 重构函数 =====

// 延迟导入 LazyMermaidRenderer 并使用单例模式
let LazyMermaidRenderer = null;
const getLazyMermaidRenderer = () => {
  if (!LazyMermaidRenderer) {
    LazyMermaidRenderer = lazy(() => import('./components/LazyMermaidRenderer'));
  }
  return LazyMermaidRenderer;
};

// ===== 三、辅助函数 =====

/**
 * 检测内容类型
 * @param {string} fileName - 文件名
 * @param {object|string} content - 文件内容
 * @param {string} senderInfo - 发送人信息（智能体名称）
 * @returns {string} 内容类型: 'markdown-table', 'markdown', 'mermaid', 'text', 'code', 'json', 'draft-diff', 'ui-sandbox', 'final-markdown'
 */
const detectContentType = (fileName, content, senderInfo = null) => {

  // 🎨 检查是否为绘图助手内容
  // 调试日志
  if (fileName && (fileName.includes('diagram') || fileName.includes('drawio'))) {
    console.log('🎨 detectContentType 调试:', { fileName, senderInfo, hasContent: !!content });
  }
  
  if (senderInfo === "diagram_draw" || fileName.endsWith('.drawio') || (fileName && fileName.startsWith('diagram_'))) {
    console.log('🎨 检测到绘图内容，返回 drawio 类型');
    return 'drawio';
  }

  // 如果没有发送人信息，继续使用原有的fileName判断逻辑
  if (fileName === "UI工程师" || fileName === "青铜版UI工程师"|| fileName === "白银版UI工程师") {
    return 'ui-sandbox';
  }
  if (fileName === "财务计算器开发者"  || fileName === "领导力财务计算器表格设计者" || fileName === "领导力定价融资表格设计者") {
    return 'excel-sandbox';
  }

  
 
  // 检查文件扩展名 - 优先检查draft类型
  if (fileName.endsWith('.draft')) {
    return 'draft-diff';
  }

  if (senderInfo === "白银版工程师"){
    return 'final-markdown';
  }
  
  // 检查是否为.final后缀文件
  if (fileName.endsWith('.final')) {
    // 直接返回final-markdown，不再进行JSON和Markdown的区分
    return 'final-markdown';
  }
  
  // 检查是否为无后缀文件（文件名中不包含点号）
  const isNoExtensionFile = !fileName.includes('.');
  
  // 无后缀文件直接当作mermaid处理
  if (isNoExtensionFile) {
    return 'final-markdown';
  }
  
  // 提取内容字符串
  const contentStr = typeof content === 'string' 
    ? content 
    : (content && typeof content === 'object' && 'content' in content)
      ? (typeof content.content === 'string' ? content.content : String(content.content || ''))
      : String(content || '');

  // 检查文件扩展名
  const isMarkdownFile = fileName.endsWith('.md') || getLanguage(fileName) === 'markdown' || getLanguage(fileName) === 'md';
  const isTextFile = fileName.endsWith('.txt') || getLanguage(fileName) === 'txt';
  const isJsonFile = fileName.endsWith('.json') || getLanguage(fileName) === 'json';

  const isMDFile = fileName.endsWith('.md');
  if (isMDFile) {
    // 定义支持的Mermaid图表类型
    const mermaidTypes = [
      "```mermaid", "<mermaid>", "graph ", "flowchart ", "sequenceDiagram", "classDiagram", 
      "mindmap", "quadrantChart", "erDiagram", "gantt", "pie", "stateDiagram", 
      "requirementDiagram", "journey", "timeline", "gitGraph", "sankey", "C4Context",
      "C4Container", "C4Component", "C4Dynamic", "C4Deployment"
    ];
    
    // 检查是否包含任何Mermaid图表类型
    const hasMermaid = mermaidTypes.some(type => contentStr.includes(type));

    // 修正：如果Markdown文件包含Mermaid，类型应为'mermaid'
    if (hasMermaid) return 'mermaid'; 
  }
  
  // 如果是Markdown文件，进一步检查内容特性
  // （保留这里的逻辑作为后备，以防上面的isMDFile检查不够全面）
  if (isMarkdownFile) {
    // 检查是否有表格
    const hasTables = contentStr.includes('|') && contentStr.includes('\n|');

    // 定义支持的Mermaid图表类型
    const mermaidTypes = [
      "```mermaid", "<mermaid>", "graph ", "flowchart ", "sequenceDiagram", "classDiagram", 
      "mindmap", "quadrantChart", "erDiagram", "gantt", "pie", "stateDiagram", 
      "requirementDiagram", "journey", "timeline", "gitGraph", "sankey", "C4Context",
      "C4Container", "C4Component", "C4Dynamic", "C4Deployment"
    ];
    
    // 检查是否包含任何Mermaid图表类型
    const hasMermaid = mermaidTypes.some(type => contentStr.includes(type));

    // 确保返回正确的类型
    if (hasMermaid) return 'mermaid';
    if (hasTables) return 'markdown-table';
    return 'markdown';
  }

  // 检查其他类型
  if (isTextFile) return 'text';
  if (isJsonFile) return 'json';


  // 默认为代码
  return 'final-markdown';
};

/**
 * 处理和提取Mermaid块
 * @param {string} content - 包含Mermaid图表的内容
 * @returns {Array} 提取的Mermaid块和非Mermaid块
 */
const processMermaidBlocks = (content) => {
    const lines = content.split('\n');
  const blocks = [];
  let currentBlock = { type: 'text', content: [], startLine: 0 };
  let inMermaid = false;
  let mermaidStartMarker = '';
  let mermaidContent = '';
  
  // 逐行分析内容
  lines.forEach((line, idx) => {
    // 检测Mermaid图表开始
    if (!inMermaid && (
        line.trim().startsWith("```mermaid") || 
        line.trim().startsWith("<mermaid>") ||
        // 处理不在代码块中但明显是Mermaid语法的行
        (line.trim().startsWith("graph ") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("flowchart ") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("sequenceDiagram") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("classDiagram") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("mindmap") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("quadrantChart") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("gantt") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("pie") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("erDiagram") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("stateDiagram") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("journey") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("timeline") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("gitGraph") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("sankey") && !line.trim().startsWith("```")) ||
        (line.trim().startsWith("requirementDiagram") && !line.trim().startsWith("```")) ||
        (line.trim().match(/^C4(Context|Container|Component|Dynamic|Deployment)/) && !line.trim().startsWith("```"))
    )) {
      // 保存之前的文本块
      if (currentBlock.content.length > 0) {
        blocks.push({...currentBlock});
      }
      // 开始新的Mermaid块
      inMermaid = true;
      
      // 判断是否有显式的Mermaid标记
      if (line.trim().startsWith("```mermaid")) {
        mermaidStartMarker = "```mermaid";
        mermaidContent = line.replace(mermaidStartMarker, '').trim() + '\n';
      } else if (line.trim().startsWith("<mermaid>")) {
        mermaidStartMarker = "<mermaid>";
        mermaidContent = line.replace(mermaidStartMarker, '').trim() + '\n';
      } else {
        // 对于没有显式标记的Mermaid内容，添加完整的Mermaid语法
        mermaidStartMarker = "implicitMermaid";
        mermaidContent = line.trim() + '\n';
      }
      
      currentBlock = { 
        type: 'mermaid', 
        content: mermaidContent, 
        startLine: idx, 
        lines: [idx],
        implicitMermaid: mermaidStartMarker === "implicitMermaid"
      };
    }
    // 检测Mermaid内容
    else if (inMermaid) {
      // 检测Mermaid图表结束
      if (
          (mermaidStartMarker === "```mermaid" && line.trim() === "```") || 
          (mermaidStartMarker === "<mermaid>" && line.trim() === "</mermaid>") ||
          // 对于隐式Mermaid块，当遇到空行或非Mermaid语法时结束
          (mermaidStartMarker === "implicitMermaid" && (
              line.trim() === "" || 
              line.trim().startsWith("#") || 
              line.trim().startsWith(">") ||
              line.trim().startsWith("- ") ||
              line.trim().startsWith("* ") ||
              line.trim().startsWith("1. ")
          ))
      ) {
        // 保存Mermaid块
        inMermaid = false;
        
        // 对于隐式Mermaid，如果结束是因为空行，则不把空行视为下一个块的一部分
        if (mermaidStartMarker === "implicitMermaid" && line.trim() === "") {
          blocks.push({...currentBlock});
          // 开始新的文本块，但不包括当前空行
          currentBlock = { type: 'text', content: [], startLine: idx + 1 };
      } else {
          blocks.push({...currentBlock});
          // 开始新的文本块，包括当前行
          currentBlock = { type: 'text', content: [line], startLine: idx };
        }
      } else {
        // 继续收集Mermaid内容
        mermaidContent += line + '\n';
        currentBlock.content = mermaidContent;
        currentBlock.lines.push(idx);
      }
    }
    // 普通文本内容
    else {
      currentBlock.content.push(line);
    }
  });
  
  // 添加最后一个块
  if (!inMermaid && currentBlock.content.length > 0) {
    blocks.push({...currentBlock});
  } else if (inMermaid) {
    // 如果Mermaid没有正确结束，也添加它
    blocks.push({...currentBlock});
  }
  
  return blocks;
};

/**
 * 分割Markdown块（表格和非表格）
 * @param {string} content - Markdown内容
 * @returns {Array} 分割的Markdown块
 */
const processMarkdownBlocks = (content) => {
  const lines = content.split('\n');
  const blocks = [];
  let currentBlock = {
    type: 'normal',
    content: [],
    startLine: 0
  };
  
  let inTable = false;
  
  lines.forEach((line, idx) => {
    // 检测表格开始
    if (!inTable && line.trim().startsWith('|') && line.trim().endsWith('|')) {
      // 如果当前有非表格内容，先保存
      if (currentBlock.content.length > 0) {
        blocks.push({...currentBlock});
        currentBlock = {
          type: 'table',
          content: [line],
          startLine: idx
        };
      } else {
        currentBlock.type = 'table';
        currentBlock.content.push(line);
      }
      inTable = true;
    }
    // 检测表格继续
    else if (inTable && line.trim().startsWith('|') && line.trim().endsWith('|')) {
      currentBlock.content.push(line);
    }
    // 检测表格结束
    else if (inTable) {
      blocks.push({...currentBlock});
      currentBlock = {
        type: 'normal',
        content: [line],
        startLine: idx
      };
      inTable = false;
    }
    // 正常行
    else {
      currentBlock.content.push(line);
    }
  });
  
  // 添加最后一个块
  if (currentBlock.content.length > 0) {
    blocks.push({...currentBlock});
  }
  
  return blocks;
};

/**
 * 创建可选择的行组件
 * @param {string} fileName - 文件名
 * @param {string} line - 行内容
 * @param {number} lineIndex - 行索引
 * @param {object} options - 选项
 * @returns {JSX.Element} 可选择的行组件
 */
const createSelectableLineComponent = (fileName, line, lineIndex, options) => {
  const { selectedFileLines, handleFileLineSelection } = options;
  
  return (
    <div key={`line-${lineIndex}`} className={fileStyles.selectableFileLine} style={{
      margin: 0, 
      padding: '2px 0',
      display: 'flex',
      alignItems: 'center',
      minHeight: '22px',
      lineHeight: '1.4'
    }}>
      <input
        type="checkbox"
        checked={(selectedFileLines[fileName] || []).includes(lineIndex)}
        onChange={(e) => handleFileLineSelection(fileName, lineIndex, e.target.checked)}
        className={fileStyles.fileLineCheckbox}
        style={{margin: '0 10px 0 0', alignSelf: 'center'}}
      />
      <div className={fileStyles.fileLineContent}>
        {line || ' '}
      </div>
    </div>
  );
};

/**
 * 创建Markdown特定组件（标题、段落等）
 * @returns {object} Markdown特定组件集合
 */
const createMarkdownComponents = () => {
  return {
    p: ({node, ...props}) => <span {...props} style={{display: 'inline', margin: 0, padding: 0, lineHeight: '1.2'}} />,
    // 添加标题样式
    h1: ({node, ...props}) => <span {...props} style={{color: '#9cdcfe', fontWeight: 'bold', fontSize: '1.5em', display: 'inline', lineHeight: '1.2'}} />,
    h2: ({node, ...props}) => <span {...props} style={{color: '#6fb9ff', fontWeight: 'bold', fontSize: '1.3em', display: 'inline', lineHeight: '1.2'}} />,
    h3: ({node, ...props}) => <span {...props} style={{color: '#569cd6', fontWeight: 'bold', fontSize: '1.2em', display: 'inline', lineHeight: '1.2'}} />,
    h4: ({node, ...props}) => <span {...props} style={{color: '#4fc1ff', fontWeight: 'bold', fontSize: '1.1em', display: 'inline', lineHeight: '1.2'}} />,
    h5: ({node, ...props}) => <span {...props} style={{color: '#36adfc', fontWeight: 'bold', fontSize: '1.05em', display: 'inline', lineHeight: '1.2'}} />,
    h6: ({node, ...props}) => <span {...props} style={{color: '#1a9bea', fontWeight: 'bold', fontSize: '1em', display: 'inline', lineHeight: '1.2'}} />,
    // 添加列表样式
    ul: ({node, ...props}) => <span {...props} style={{display: 'inline', margin: 0, padding: 0, lineHeight: '1.2'}} />,
    ol: ({node, ...props}) => <span {...props} style={{display: 'inline', margin: 0, padding: 0, lineHeight: '1.2'}} />,
    li: ({node, ...props}) => <span {...props} style={{display: 'inline', margin: 0, padding: 0, lineHeight: '1.2'}} />,
    // 引用样式
    blockquote: ({node, ...props}) => <span {...props} style={{
      display: 'inline', 
      margin: 0, 
      padding: 0, 
      lineHeight: '1.2',
      color: '#b3b3b3',
      borderLeft: '3px solid #555',
      paddingLeft: '8px'
    }} />,
    // 代码样式
    code({node, inline, className, children, ...props}) {
      const match = /language-(\w+)/.exec(className || "");
      return !inline && match ? (
        <span style={{display: 'inline'}}>
          <SyntaxHighlighter
            language={match[1]}
            style={vscDarkPlus}
            wrapLines={true}
            wrapLongLines={true}
            customStyle={{display: 'inline', margin: 0, padding: '0 4px'}}
          >
            {String(children).replace(/\n$/, "")}
          </SyntaxHighlighter>
        </span>
      ) : (
        <code 
          className={className} 
          {...props}
          style={{
            backgroundColor: '#2d2d2d',
            padding: '0.2em 0.4em',
            borderRadius: '3px',
            fontFamily: 'Consolas, Monaco, monospace',
            fontSize: '0.9em',
            color: '#ce9178'
          }}
        >
          {children}
        </code>
      );
    }
  };
};

// ===== 四、特定组件渲染函数 =====

/**
 * 渲染表格组件
 * @param {string} tableContent - 表格内容
 * @param {string} fileName - 文件名
 * @param {number} blockStartLine - 块起始行
 * @param {object} options - 选项
 * @returns {JSX.Element} 表格组件
 */
const renderTableComponent = (tableContent, fileName, blockStartLine, options) => {
  const { selectedFileLines, handleFileLineSelection } = options;
  
  return (
    <div className={fileStyles.markdownTableContainer}>
      <div className={fileStyles.tableSelectOverlay}>
        {/* 只显示一个选择框，用于选择整个表格 */}
        <div className={fileStyles.tableSelectLine}>
          <input
            type="checkbox"
            checked={(selectedFileLines[fileName] || []).includes(blockStartLine)}
            onChange={(e) => {
              // 如果选中，则选择表格的所有行
              // 如果取消选中，则取消选择表格的所有行
              const lineIndices = Array.from(
                {length: tableContent.split('\n').length}, 
                (_, i) => blockStartLine + i
              );
              
              // 根据复选框状态，选择或取消选择所有行
              if (e.target.checked) {
                // 逐行添加所有表格行
                lineIndices.forEach(lineIdx => {
                  handleFileLineSelection(fileName, lineIdx, true);
                });
              } else {
                // 逐行移除所有表格行
                lineIndices.forEach(lineIdx => {
                  handleFileLineSelection(fileName, lineIdx, false);
                });
              }
            }}
            className={fileStyles.fileLineCheckbox}
          />
        </div>
      </div>
      <div className={fileStyles.markdownTable}>
        {/* 使用 UnifiedContentRenderer 渲染表格，确保支持数学公式 */}
        <UnifiedContentRenderer 
          content={tableContent}
          collapsible={false}
          renderOptions={{
            customClass: fileStyles.markdownTable,
            // 传递特定的表格样式
            tableClass: fileStyles.markdownTable,
            showDebugInfo: false,
            compactMode: true
          }}
        />
      </div>
    </div>
  );
};

/**
 * 渲染图表组件（使用LazyMermaidRenderer）
 * @param {string} chart - 图表内容
 * @param {number} lineIndex - 行索引
 * @param {string} fileName - 文件名
 * @param {object} options - 选项
 * @returns {JSX.Element} 图表组件
 */
const renderMermaidComponent = (chart, lineIndex, fileName, options) => {
  // 使用统一渲染逻辑的组件
  return (
    <div className={fileStyles.mermaidWrapper} key={`mermaid-${lineIndex}`}>
      <UnifiedContentRenderer 
        content={chart}
        className={fileStyles.mermaidRenderer}
        collapsible={false}
        renderOptions={{
          customClass: fileStyles.fileContentMermaid,
          nestingLevel: 0,
          showDebugInfo: ENABLE_MERMAID_DEBUG,
          mermaidOptions: {
            theme: 'dark',
            securityLevel: 'loose',
            flowchart: { curve: 'basis' }
          }
        }}
      />
    </div>
  );
};

/**
 * 渲染单行Markdown内容
 * @param {string} line - 行内容
 * @param {boolean} isListItem - 是否是列表项
 * @returns {JSX.Element} Markdown行组件
 */
const renderMarkdownLine = (line, isListItem = false) => {
  if (line.trim() === '') {
    return <span style={{lineHeight: '1.2'}}>&nbsp;</span>;
  }
  
  // 检查是否为引用行（以'>'开头）
  const isQuote = line.trim().startsWith('>');
  
  if (isQuote) {
    // 对引用内容应用特殊样式
    return (
      <span style={{
        lineHeight: '1.2',
        display: 'inline',
        whiteSpace: 'pre-wrap',
        color: '#b3b3b3', // 稍微变淡的颜色
        borderLeft: '3px solid #555',
        paddingLeft: '8px',
        marginLeft: '-4px'
      }}>
        {line}
      </span>
    );
  }
  
  if (isListItem) {
    return (
      <span style={{
        lineHeight: '1.2',
        display: 'inline',
        whiteSpace: 'pre-wrap'
      }}>
        {line}
      </span>
    );
  }
  
  // 使用 UnifiedContentRenderer 替代手动配置的 ReactMarkdown
  // 这样可以确保左右侧渲染逻辑完全一致，并正确支持数学公式
  return (
    <UnifiedContentRenderer 
      content={line}
      className={fileStyles.inlineContent}
      renderOptions={{
        customClass: '',
        inlineMode: true, // 强制内联模式
        nestingLevel: 0,
        showDebugInfo: false,
        compactMode: true // 启用紧凑模式
      }}
    />
  );
};

// ===== 二、专门的渲染器函数 =====

/**
 * 处理包含表格的Markdown文件
 * @param {string} fileName - 文件名
 * @param {string} content - 文件内容
 * @param {object} options - 渲染选项
 * @returns {JSX.Element} 渲染结果
 */
const renderMarkdownWithTables = (fileName, content, options) => {
  const { selectedFileLines, handleFileLineSelection } = options;
  const blocks = processMarkdownBlocks(content);
  
  return (
    <div className={fileStyles.selectableLinesContainer} data-file-name={fileName} style={{margin: 0, padding: 0}}>
      {blocks.map(block => {
        if (block.type === 'table') {
          // 对表格块进行整体渲染，但保持每行可选
          const tableContent = block.content.join('\n');
          return renderTableComponent(tableContent, fileName, block.startLine, options);
        } else {
          // 对普通块进行逐行渲染
          return (
            <React.Fragment key={`block-${block.startLine}`}>
              {block.content.map((line, idx) => {
                const lineIndex = block.startLine + idx;
                const isListItem = line.trim().match(/^(-|\*|\d+\.)\s/);
                
                return (
                  <div key={`line-${lineIndex}`} className={fileStyles.selectableFileLine} style={{
                    margin: 0, 
                    padding: '2px 0',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '22px',
                    lineHeight: '1.4'
                  }}>
                    <input
                      type="checkbox"
                      checked={(selectedFileLines[fileName] || []).includes(lineIndex)}
                      onChange={(e) => handleFileLineSelection(fileName, lineIndex, e.target.checked)}
                      className={fileStyles.fileLineCheckbox}
                      style={{margin: '0 10px 0 0', alignSelf: 'center'}}
                    />
                    <div className={fileStyles.fileLineContent}>
                      {renderMarkdownLine(line, isListItem)}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        }
      })}
    </div>
  );
};

/**
 * 处理不包含表格的普通Markdown文件
 * @param {string} fileName - 文件名
 * @param {string} content - 文件内容
 * @param {object} options - 渲染选项
 * @returns {JSX.Element} 渲染结果
 */
const renderPureMarkdown = (fileName, content, options) => {
  const { selectedFileLines, handleFileLineSelection } = options;
  const lines = content.split('\n');

  return (
    <div className={fileStyles.selectableLinesContainer} data-file-name={fileName} style={{margin: 0, padding: 0}}>
      {lines.map((line, lineIndex) => {
        const isListItem = line.trim().match(/^(-|\*|\d+\.)\s/);
        const isQuote = line.trim().startsWith('>');
        
        // 根据行类型确定行高和内边距
        const lineStyle = {
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          lineHeight: '1.2'
        };
        
        // 对引用行使用更紧凑的样式
        if (isQuote) {
          lineStyle.padding = '0';
          lineStyle.minHeight = '18px';
        } else {
          lineStyle.padding = '1px 0';
          lineStyle.minHeight = '20px';
        }
        
        return (
          <div key={`line-${lineIndex}`} className={fileStyles.selectableFileLine} style={lineStyle}>
            <input
              type="checkbox"
              checked={(selectedFileLines[fileName] || []).includes(lineIndex)}
              onChange={(e) => handleFileLineSelection(fileName, lineIndex, e.target.checked)}
              className={fileStyles.fileLineCheckbox}
              style={{margin: '0 10px 0 0', alignSelf: 'center'}}
            />
            <div className={fileStyles.fileLineContent}>
              {renderMarkdownLine(line, isListItem)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * 🎨 处理 Draw.io 图表内容
 * @param {string} fileName - 文件名
 * @param {string} content - Draw.io XML 内容
 * @param {object} options - 渲染选项
 * @returns {JSX.Element} 渲染结果
 */
/**
 * 🎨 Draw.io 对话消息显示组件
 */
const DiagramChatMessages = ({ messages }) => {
  if (!messages || messages.length === 0) {
    return null;
  }

  return (
    <div style={{
      padding: '16px',
      backgroundColor: 'rgba(30, 30, 30, 0.6)',
      borderRadius: '8px',
      marginTop: '16px',
      maxHeight: '300px',
      overflowY: 'auto'
    }}>
      <div style={{
        fontSize: '12px',
        color: '#94a3b8',
        marginBottom: '12px',
        fontWeight: '500'
      }}>
        💬 绘图对话
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {messages.map((msg, index) => (
          <div
            key={msg.id || index}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
            }}
          >
            <div style={{
              maxWidth: '80%',
              padding: '10px 14px',
              borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
              backgroundColor: msg.role === 'user' 
                ? 'rgba(77, 139, 255, 0.2)' 
                : msg.isError 
                  ? 'rgba(239, 68, 68, 0.2)' 
                  : 'rgba(255, 255, 255, 0.08)',
              border: msg.role === 'user'
                ? '1px solid rgba(77, 139, 255, 0.3)'
                : msg.isError
                  ? '1px solid rgba(239, 68, 68, 0.3)'
                  : '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              {/* 角色标签 */}
              <div style={{
                fontSize: '10px',
                color: msg.role === 'user' ? '#4D8BFF' : msg.isError ? '#ef4444' : '#10b981',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                {msg.role === 'user' ? '你' : '🎨 绘图助手'}
              </div>
              {/* 消息内容 */}
              <div style={{
                fontSize: '13px',
                color: msg.isError ? '#fca5a5' : '#e2e8f0',
                lineHeight: '1.5',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {msg.content}
              </div>
              {/* 如果有工具调用，显示状态 */}
              {msg.hasToolCall && msg.xml && (
                <div style={{
                  marginTop: '8px',
                  padding: '6px 10px',
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '6px',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ fontSize: '12px' }}>✅</span>
                  <span style={{ fontSize: '11px', color: '#10b981' }}>图表已生成</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * 🎨 Draw.io 图表渲染组件
 * 直接使用 iframe 嵌入 draw.io，避免 react-drawio 的焦点问题
 */
const DrawioRenderer = ({ content, messages }) => {
  const iframeRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [isEditorReady, setIsEditorReady] = React.useState(false);
  const [isExporting, setIsExporting] = React.useState(false);
  const [isChatOpen, setIsChatOpen] = React.useState(false); // 🆕 聊天抽屉状态
  const previousXmlRef = React.useRef('');
  const pendingXmlRef = React.useRef('');
  const exportResolverRef = React.useRef(null);

  // 处理 XML 格式，确保符合 draw.io 要求
  // draw.io 需要 <mxfile> 格式，不能直接用 <mxGraphModel>
  const processXml = React.useCallback((rawXml) => {
    if (!rawXml || rawXml.trim() === '') return '';
    
    let xml = rawXml.trim();
    
    // 如果已经是 mxfile 格式，直接返回
    if (xml.includes('<mxfile')) {
      return xml;
    }
    
    // 如果是 mxGraphModel 格式，需要包装成 mxfile
    if (xml.includes('<mxGraphModel')) {
      // 提取 mxGraphModel 内容
      const mxGraphModelMatch = xml.match(/<mxGraphModel[^>]*>([\s\S]*)<\/mxGraphModel>/);
      if (mxGraphModelMatch) {
        const innerContent = mxGraphModelMatch[0];
        return `<mxfile><diagram name="Page-1" id="page-1">${innerContent}</diagram></mxfile>`;
      }
    }
    
    // 如果只有 <root> 标签，包装成完整格式
    if (xml.startsWith('<root>')) {
      const rootContent = xml.slice(6, -7); // 移除 <root> 和 </root>
      return `<mxfile><diagram name="Page-1" id="page-1"><mxGraphModel><root>${rootContent}</root></mxGraphModel></diagram></mxfile>`;
    }
    
    // 如果只有 mxCell 标签，包装成完整格式
    if (xml.includes('<mxCell')) {
      return `<mxfile><diagram name="Page-1" id="page-1"><mxGraphModel><root><mxCell id="0"/><mxCell id="1" parent="0"/>${xml}</root></mxGraphModel></diagram></mxfile>`;
    }
    
    return xml;
  }, []);

  // 向 draw.io iframe 发送消息
  const sendMessageToDrawio = React.useCallback((action, data = {}) => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      const message = JSON.stringify({ action, ...data });
      iframeRef.current.contentWindow.postMessage(message, '*');
    }
  }, []);

  // 加载图表到 draw.io
  const loadDiagram = React.useCallback((xml) => {
    if (isEditorReady && xml && xml !== previousXmlRef.current) {
      console.log('🎨 DrawioRenderer: 发送 load 消息到 draw.io');
      sendMessageToDrawio('load', { xml, autosave: 0 });
      previousXmlRef.current = xml;
    } else if (!isEditorReady && xml) {
      // 编辑器还没准备好，保存待加载的 XML
      pendingXmlRef.current = xml;
    }
  }, [isEditorReady, sendMessageToDrawio]);

  // 监听来自 draw.io 的消息
  React.useEffect(() => {
    const handleMessage = (event) => {
      // 只处理来自 draw.io 的消息
      if (event.data && typeof event.data === 'string') {
        try {
          const msg = JSON.parse(event.data);
          
          if (msg.event === 'init') {
            console.log('🎨 DrawioRenderer: draw.io 编辑器已初始化');
            setIsEditorReady(true);
            
            // 如果有待加载的 XML，现在加载
            if (pendingXmlRef.current) {
              setTimeout(() => {
                loadDiagram(pendingXmlRef.current);
                pendingXmlRef.current = '';
              }, 100);
            }
          } else if (msg.event === 'load') {
            console.log('🎨 DrawioRenderer: 图表已加载');
          } else if (msg.event === 'save') {
            console.log('🎨 DrawioRenderer: 图表已保存');
          } else if (msg.event === 'export') {
            console.log('🎨 DrawioRenderer: 图表已导出', msg.format);
            setIsExporting(false);
            
            // 处理导出的数据
            if (msg.data && exportResolverRef.current) {
              exportResolverRef.current(msg.data);
              exportResolverRef.current = null;
            }
          }
        } catch (e) {
          // 忽略非 JSON 消息
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loadDiagram]);

  // 当 content 变化时，加载图表
  React.useEffect(() => {
    if (content && content.trim() !== '') {
      const processedXml = processXml(content);
      if (processedXml) {
        loadDiagram(processedXml);
      }
    }
  }, [content, processXml, loadDiagram]);

  // 导出为 PNG
  const exportToPng = React.useCallback(() => {
    if (!isEditorReady || !iframeRef.current) {
      console.warn('🎨 DrawioRenderer: 编辑器未就绪，无法导出');
      return;
    }
    
    setIsExporting(true);
    
    // 创建 Promise 来等待导出结果
    const exportPromise = new Promise((resolve, reject) => {
      exportResolverRef.current = resolve;
      
      // 超时处理
      setTimeout(() => {
        if (exportResolverRef.current) {
          exportResolverRef.current = null;
          setIsExporting(false);
          reject(new Error('导出超时'));
        }
      }, 10000);
    });
    
    // 发送导出命令
    const message = JSON.stringify({
      action: 'export',
      format: 'png',
      spin: 'Exporting...'
    });
    iframeRef.current.contentWindow.postMessage(message, '*');
    
    // 处理导出结果
    exportPromise.then((data) => {
      // data 是 base64 编码的 PNG 数据
      const link = document.createElement('a');
      link.download = `diagram_${Date.now()}.png`;
      link.href = data;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      console.log('🎨 DrawioRenderer: PNG 导出成功');
    }).catch((error) => {
      console.error('🎨 DrawioRenderer: PNG 导出失败:', error);
    });
  }, [isEditorReady]);

  // 导出为 drawio 格式（XML）
  const exportToDrawio = React.useCallback(() => {
    if (!isEditorReady || !iframeRef.current) {
      console.warn('🎨 DrawioRenderer: 编辑器未就绪，无法导出');
      return;
    }
    
    setIsExporting(true);
    
    const exportPromise = new Promise((resolve, reject) => {
      exportResolverRef.current = resolve;
      setTimeout(() => {
        if (exportResolverRef.current) {
          exportResolverRef.current = null;
          setIsExporting(false);
          reject(new Error('导出超时'));
        }
      }, 10000);
    });
    
    // 导出为 xmlsvg 格式，这样可以获取完整的 XML
    const message = JSON.stringify({
      action: 'export',
      format: 'xmlsvg',
      spin: 'Exporting...'
    });
    iframeRef.current.contentWindow.postMessage(message, '*');
    
    exportPromise.then((data) => {
      // 从 data URL 中提取 XML 内容
      // xmlsvg 格式返回的是 data:image/svg+xml;base64,... 
      // 我们需要解码并提取 XML
      try {
        let xmlContent = content; // 使用当前的 XML 内容
        
        // 确保是 mxfile 格式
        if (!xmlContent.includes('<mxfile')) {
          const processedXml = processXml(xmlContent);
          xmlContent = processedXml;
        }
        
        const blob = new Blob([xmlContent], { type: 'application/xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `diagram_${Date.now()}.drawio`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log('🎨 DrawioRenderer: drawio 导出成功');
      } catch (error) {
        console.error('🎨 DrawioRenderer: drawio 导出失败:', error);
      }
      setIsExporting(false);
    }).catch((error) => {
      console.error('🎨 DrawioRenderer: drawio 导出失败:', error);
      setIsExporting(false);
    });
  }, [isEditorReady, content, processXml]);

  // 构建 draw.io URL
  const drawioUrl = React.useMemo(() => {
    const params = new URLSearchParams({
      embed: '1',
      spin: '1',
      proto: 'json',
      libraries: '0',        // 不显示形状库
      saveAndExit: '0',      // 不显示保存并退出
      noExitBtn: '1',        // 不显示退出按钮
      noSaveBtn: '1',        // 🎨 隐藏保存按钮
      modified: 'unsavedChanges',
      // 🎨 默认关闭左侧形状面板和右侧格式面板
      format: '0',           // 关闭右侧格式/样式面板
      picker: '0',           // 关闭颜色选择器
      pages: '0',            // 不显示多页面标签
      ui: 'min'              // 使用最小化 UI（隐藏左侧面板）
    });
    return `https://embed.diagrams.net/?${params.toString()}`;
  }, []);

  // 如果内容为空，显示占位符
  if (!content || content.trim() === '') {
    return (
      <div className={fileStyles.drawioContainer} style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        backgroundColor: 'rgba(30, 30, 30, 0.5)',
        borderRadius: '0', // 🎨 统一移除圆角
        border: 'none',    // 🎨 统一移除边框
        position: 'relative', 
        overflow: 'hidden'
      }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '48px', marginBottom: '16px' }}>🎨</span>
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>
            在下方输入框中描述您想要的图表，AI将为您生成
          </p>
          <p style={{ color: '#64748b', fontSize: '12px', marginTop: '8px' }}>
            例如："画一个用户登录流程图" 或 "创建系统架构图"
          </p>
        </div>

        {/* 🆕 底部悬浮聊天抽屉 - 优化UI (占位符模式) */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          pointerEvents: 'none', 
          zIndex: 900,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          height: '100%'
        }}>
          {/* 展开后的面板 */}
          <div style={{
            width: '100%',
            backgroundColor: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isChatOpen ? 'translateY(0)' : 'translateY(100%)',
            height: '320px',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0
          }}>
            {/* 面板标题栏 */}
            <div 
              onClick={() => setIsChatOpen(false)}
              style={{
                height: '40px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderBottom: '1px solid #f1f5f9',
                backgroundColor: '#ffffff',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                  对话历史
                </span>
                <span style={{ 
                  fontSize: '11px', 
                  backgroundColor: '#f1f5f9', 
                  color: '#64748b',
                  padding: '2px 6px', 
                  borderRadius: '10px' 
                }}>
                  {messages?.length || 0}
                </span>
              </div>
              <span style={{ color: '#94a3b8', fontSize: '18px', lineHeight: 1 }}>×</span>
            </div>

            {/* 聊天内容 */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#f8fafc'
            }}>
              <DiagramChatMessages messages={messages} />
            </div>
          </div>

          {/* 悬浮胶囊按钮 */}
          <div 
            onClick={() => setIsChatOpen(true)}
            style={{
              marginBottom: '16px',
              backgroundColor: 'rgba(30, 30, 30, 0.9)',
              padding: '8px 16px',
              borderRadius: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              pointerEvents: 'auto',
              transition: 'all 0.2s ease',
              transform: isChatOpen ? 'translateY(100px)' : 'translateY(0)',
              opacity: isChatOpen ? 0 : 1
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '16px' }}>💬</span>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#ffffff' }}>
              对话历史
            </span>
            {messages?.length > 0 && (
              <span style={{ 
                fontSize: '11px', 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: '#ffffff',
                padding: '1px 6px', 
                borderRadius: '10px',
                marginLeft: '4px'
              }}>
                {messages.length}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Draw.io 编辑器容器 */}
      <div 
        ref={containerRef}
        className={fileStyles.drawioContainer} 
        style={{
          width: '100%',
          height: '600px',
          backgroundColor: '#ffffff',
          // 🎨 移除圆角，消除与左侧面板连接处的缝隙，实现无缝拼接
          borderRadius: '0',
          margin: 0,
          // 🎨 移除边框，因为已经填满容器，无需额外边框
          border: 'none',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* 🎨 导出按钮 - 微调垂直位置 */}
        <div style={{
          position: 'absolute',
          top: '10px', // ⬇️ 稍微下移，与左侧工具栏视觉中心更对齐
          right: '10px',
          zIndex: 1000,
          display: 'flex',
          gap: '6px',
          backgroundColor: 'transparent',
          padding: 0,
        }}>
          <button
            onClick={exportToPng}
            disabled={!isEditorReady || isExporting}
            title="导出为 PNG 图片"
            style={{
              padding: '5px 10px',
              // 🎨 模仿 draw.io 原生工具栏按钮样式 (浅灰背景)
              backgroundColor: '#f1f3f4',
              border: '1px solid transparent',
              borderRadius: '4px', // draw.io 风格圆角
              color: '#333',
              fontSize: '12px',
              cursor: isEditorReady && !isExporting ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
            onMouseEnter={(e) => {
              if(!isExporting) {
                e.currentTarget.style.backgroundColor = '#e0e3e5';
                e.currentTarget.style.borderColor = '#d0d3d5';
              }
            }}
            onMouseLeave={(e) => {
              if(!isExporting) {
                e.currentTarget.style.backgroundColor = '#f1f3f4';
                e.currentTarget.style.borderColor = 'transparent';
              }
            }}
          >
            <span style={{ fontSize: '14px' }}>📷</span>
            <span>PNG</span>
          </button>
          <button
            onClick={exportToDrawio}
            disabled={!isEditorReady || isExporting}
            title="导出为 .drawio 文件"
            style={{
              padding: '5px 10px',
              backgroundColor: '#f1f3f4',
              border: '1px solid transparent',
              borderRadius: '4px',
              color: '#333',
              fontSize: '12px',
              cursor: isEditorReady && !isExporting ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
            onMouseEnter={(e) => {
              if(!isExporting) {
                e.currentTarget.style.backgroundColor = '#e0e3e5';
                e.currentTarget.style.borderColor = '#d0d3d5';
              }
            }}
            onMouseLeave={(e) => {
              if(!isExporting) {
                e.currentTarget.style.backgroundColor = '#f1f3f4';
                e.currentTarget.style.borderColor = 'transparent';
              }
            }}
          >
            <span style={{ fontSize: '14px' }}>💾</span>
            <span>Drawio</span>
          </button>
        </div>
        
        <iframe
          ref={iframeRef}
          src={drawioUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          title="Draw.io Editor"
          allow="clipboard-read; clipboard-write"
        />

        {/* 🆕 底部悬浮聊天抽屉 - 优化UI */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          pointerEvents: 'none', // 允许点击穿透到 iframe
          zIndex: 900,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-end',
          height: '100%'
        }}>
          {/* 展开后的面板 */}
          <div style={{
            width: '100%',
            backgroundColor: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isChatOpen ? 'translateY(0)' : 'translateY(100%)',
            height: '320px',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto', // 恢复点击
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0
          }}>
            {/* 面板标题栏 */}
            <div 
              onClick={() => setIsChatOpen(false)}
              style={{
                height: '40px',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                borderBottom: '1px solid #f1f5f9',
                backgroundColor: '#ffffff',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                  对话历史
                </span>
                <span style={{ 
                  fontSize: '11px', 
                  backgroundColor: '#f1f5f9', 
                  color: '#64748b',
                  padding: '2px 6px', 
                  borderRadius: '10px' 
                }}>
                  {messages?.length || 0}
                </span>
              </div>
              <span style={{ color: '#94a3b8', fontSize: '18px', lineHeight: 1 }}>×</span>
            </div>

            {/* 聊天内容 */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#f8fafc'
            }}>
              <DiagramChatMessages messages={messages} />
            </div>
          </div>

          {/* 悬浮胶囊按钮 (仅在收起时显示) */}
          <div 
            onClick={() => setIsChatOpen(true)}
            style={{
              marginBottom: '24px', // ⬆️ 增加底部间距，避开底部滚动条和页码
              backgroundColor: 'rgba(30, 30, 30, 0.9)', 
              padding: '8px 16px',
              borderRadius: '20px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              pointerEvents: 'auto',
              transition: 'all 0.2s ease',
              transform: isChatOpen ? 'translateY(100px)' : 'translateY(0)', 
              opacity: isChatOpen ? 0 : 1
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <span style={{ fontSize: '16px' }}>💬</span>
            <span style={{ fontSize: '13px', fontWeight: 500, color: '#ffffff' }}>
              对话历史
            </span>
            {messages?.length > 0 && (
              <span style={{ 
                fontSize: '11px', 
                backgroundColor: 'rgba(255,255,255,0.2)', 
                color: '#ffffff',
                padding: '1px 6px', 
                borderRadius: '10px',
                marginLeft: '4px'
              }}>
                {messages.length}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const renderDrawioContent = (fileName, content, options) => {
  console.log('🎨 renderDrawioContent 被调用:', { fileName, contentLength: content?.length });
  
  // 从 options 中获取消息历史
  const messages = options?.messages || [];
  
  // 使用 DrawioRenderer 组件，它会通过 ref 加载图表
  return <DrawioRenderer content={content} messages={messages} />;
};

/**
 * 处理Mermaid图表内容
 * @param {string} fileName - 文件名
 * @param {string} content - 文件内容
 * @param {object} options - 渲染选项
 * @returns {JSX.Element} 渲染结果
 */
const renderMermaidContent = (fileName, content, options) => {
  // 使用统一的内容处理逻辑
  const { processContent } = require('./components/core/ContentUtils');
  const cacheKey = generateCacheKey(fileName, content);
  
  // 检查缓存
  if (renderedContentCache.has(cacheKey)) {
    return renderedContentCache.get(cacheKey);
  }
  
  // 将整个内容作为块进行处理，以支持行选择功能
  const blocks = processMermaidBlocks(content);
  
  const renderedContent = (
    <div className={fileStyles.selectableLinesContainer} data-file-name={fileName} style={{margin: 0, padding: 0}}>
      {blocks.map((block, blockIdx) => {
        if (block.type === 'mermaid') {
          // 使用统一渲染器渲染Mermaid图表块
          return (
            <div key={`mermaid-${blockIdx}`} className={fileStyles.mermaidBlock}>
              <UnifiedContentRenderer 
                content={block.content}
                className={fileStyles.mermaidRenderer}
                collapsible={false}
                renderOptions={{
                  customClass: fileStyles.fileContentMermaid,
                  nestingLevel: 0,
                  showDebugInfo: ENABLE_MERMAID_DEBUG,
                  // 增加图表渲染选项
                  mermaidOptions: {
                    theme: 'dark',
                    securityLevel: 'loose',
                    flowchart: { curve: 'basis' }
                  }
                }}
              />
              {/* 在图表下方添加整体选择功能 */}
              <div className={fileStyles.selectableFileLine} style={{
                margin: '8px 0 0 0',
                padding: '6px 0',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'rgba(30, 30, 30, 0.4)',
                borderRadius: '4px'
              }}>
                <input
                  type="checkbox"
                  checked={(options.selectedFileLines[fileName] || []).includes(block.startLine)}
                  onChange={(e) => options.handleFileLineSelection(fileName, block.startLine, e.target.checked)}
                  className={fileStyles.fileLineCheckbox}
                  style={{margin: '0 10px 0 0', alignSelf: 'center'}}
                />
                <div className={fileStyles.fileLineContent} style={{fontSize: '0.85rem'}}>
                  选择此图表
                </div>
              </div>
            </div>
          );
        } else if (block.content.length > 0) {
          // 渲染文本块，使用统一渲染器处理Markdown内容
          const lines = Array.isArray(block.content) ? block.content : block.content.split('\n');
          
          return (
            <React.Fragment key={`text-${blockIdx}`}>
              {lines.map((line, lineIdx) => {
                const globalLineIdx = block.startLine + lineIdx;
                
                return (
                  <div key={`line-${globalLineIdx}`} className={fileStyles.selectableFileLine} style={{
                    margin: 0, 
                    padding: '2px 0',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '22px',
                    lineHeight: '1.4'
                  }}>
                    <input
                      type="checkbox"
                      checked={(options.selectedFileLines[fileName] || []).includes(globalLineIdx)}
                      onChange={(e) => options.handleFileLineSelection(fileName, globalLineIdx, e.target.checked)}
                      className={fileStyles.fileLineCheckbox}
                      style={{margin: '0 10px 0 0', alignSelf: 'center'}}
                    />
                    <div className={fileStyles.fileLineContent}>
                      <UnifiedContentRenderer 
                        content={line}
                        className={fileStyles.inlineContent}
                        renderOptions={{
                          customClass: '',
                          inlineMode: true,
                          nestingLevel: 0,
                          showDebugInfo: false // 内联模式不显示调试信息
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          );
        }
        return null;
      })}
    </div>
  );
  
  // 存入缓存
  renderedContentCache.set(cacheKey, renderedContent);
  return renderedContent;
};

/**
 * 处理带行号和选择框的普通文本
 * @param {string} fileName - 文件名
 * @param {string} content - 文件内容
 * @param {object} options - 渲染选项
 * @returns {JSX.Element} 渲染结果
 */
const renderPlainTextWithLines = (fileName, content, options) => {
  const { selectedFileLines, handleFileLineSelection } = options;
  const lines = content.split('\n');
  
  return (
    <div className={fileStyles.selectableLinesContainer} style={{margin: 0, padding: 0}}>
      {lines.map((line, lineIndex) => 
        createSelectableLineComponent(fileName, line, lineIndex, options)
      )}
    </div>
  );
};

/**
 * 处理需要语法高亮的代码
 * @param {string} fileName - 文件名
 * @param {string} content - 文件内容
 * @param {object} options - 渲染选项
 * @returns {JSX.Element} 渲染结果
 */
const renderCodeHighlight = (fileName, content, options) => {
  const language = getLanguage(fileName);
  
  return (
    <SyntaxHighlighter
      language={language}
      style={vscDarkPlus}
      wrapLines={true}
      wrapLongLines={true}
      className={fileStyles.syntaxHighlighter}
    >
      {content}
    </SyntaxHighlighter>
  );
};

/**
 * 处理JSON格式内容
 * @param {string} fileName - 文件名
 * @param {object|string} content - JSON内容
 * @param {object} options - 渲染选项
 * @returns {JSX.Element} 渲染结果
 */
const renderJsonContent = (fileName, content, options) => {
  // 创建一个基于文件名的唯一key，确保不同文件之间切换时组件被完全重新创建
  const componentKey = `json-renderer-${fileName.replace(/[^a-zA-Z0-9]/g, '-')}`;
  
  // 使用JsonContentRenderer组件来替代直接使用Hooks
  return <JsonContentRenderer 
    key={componentKey}
    fileName={fileName} 
    content={content} 
    options={options} 
  />;
};

/**
 * JSON内容渲染器组件 - 将Hooks逻辑移至此React组件中
 */
const JsonContentRenderer = React.memo(({ fileName, content, options }) => {
  const [renderedContent, setRenderedContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // 添加ref来跟踪组件是否已加载过
  const hasLoadedRef = React.useRef(false);
  // 使用ref保存上次渲染的内容标识符
  const lastContentRef = React.useRef(typeof content === 'object' ? JSON.stringify(content) : content);
  // 使用ref存储当前的缓存键
  const cacheKeyRef = React.useRef('');
  
  // 生成缓存键
  const cacheKey = generateCacheKey(fileName, content);
  
  // 更新缓存键引用
  if (cacheKey !== cacheKeyRef.current) {
    cacheKeyRef.current = cacheKey;
  }
  
  // 缓存JsonRenderer实例
  const JsonRenderer = lazy(() => import('./format-json-content'));
  
  // 检查内容是否真正需要重新渲染
  const contentHash = typeof content === 'object' 
    ? JSON.stringify(content).substring(0, 200) // 只用前200个字符比较
    : content.substring(0, 200); 
  
  const contentChanged = contentHash !== lastContentRef.current.substring(0, 200);
  
  // 使用useEffect确保只在组件挂载或content变化时加载
  useEffect(() => {
    // 保存当前渲染的组件实例ID，用于防止异步渲染错误
    const renderInstanceId = Math.random().toString(36).substring(2, 9);
    
    // 打印调试信息
    console.log(`[JSON Renderer] ${fileName} - 缓存键: ${cacheKey}`);
    
    // 检查是否命中缓存
    const cachedContent = renderedContentCache.get(cacheKey);
    if (cachedContent) {
      console.log(`[JSON Renderer] ${fileName} - 缓存命中`);
      setRenderedContent(cachedContent);
      setIsLoading(false);
      hasLoadedRef.current = true;
      lastContentRef.current = contentHash;
      return;
    }
    
    console.log(`[JSON Renderer] ${fileName} - 缓存未命中，重新渲染`);
    
    // 需要重新渲染
    let isMounted = true;
    setIsLoading(true);
    
    // 使用异步延迟允许DOM完全更新
    const timer = setTimeout(() => {
      if (!isMounted) return;
      
      try {
        console.log(`[JSON Renderer] ${fileName} - 创建新内容 (${renderInstanceId})`);
        const newContent = <JsonRenderer content={content} fileName={fileName} />;
        
        // 确保组件仍然挂载
        if (!isMounted) return;
        
        // 更新组件状态
        setRenderedContent(newContent);
        
        // 存入缓存 - 先清理相关缓存减少内存占用
        const maxCacheSize = 10; // 最多缓存10个条目
        if (renderedContentCache.size >= maxCacheSize) {
          // 查找相同文件名但不同内容的缓存项删除
          const keysToDelete = [];
          renderedContentCache.forEach((_, key) => {
            if (key.startsWith(fileName.split('/').pop().split('\\').pop())) {
              if (key !== cacheKey) {
                keysToDelete.push(key);
              }
            }
          });
          
          // 如果找到了相关缓存，删除它们
          if (keysToDelete.length > 0) {
            keysToDelete.forEach(key => renderedContentCache.delete(key));
          } 
          // 如果没有找到相关缓存，删除最旧的缓存项
          else if (renderedContentCache.size >= maxCacheSize) {
            const oldestKey = Array.from(renderedContentCache.keys())[0];
            renderedContentCache.delete(oldestKey);
          }
        }
        
        // 保存到缓存
        renderedContentCache.set(cacheKey, newContent);
        console.log(`[JSON Renderer] ${fileName} - 缓存已更新 (${renderInstanceId})`);
        
        // 更新状态
        setIsLoading(false);
        hasLoadedRef.current = true;
        lastContentRef.current = contentHash;
      } catch (error) {
        console.error(`[JSON Renderer] ${fileName} - 渲染错误:`, error);
        // 出错时设置加载完成但不更新缓存
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }, 50);
    
    return () => {
      isMounted = false;
      clearTimeout(timer);
      console.log(`[JSON Renderer] ${fileName} - 组件卸载 (${renderInstanceId})`);
    };
  }, [fileName, cacheKey, contentChanged]);
  
  // 如果已加载过且内容没有变化，直接使用之前渲染的内容
  if (hasLoadedRef.current && !contentChanged && renderedContent) {
    return (
      <Suspense fallback={<div>Loading JSON renderer...</div>}>
        {renderedContent}
      </Suspense>
    );
  }
  
  // 如果有缓存，直接使用缓存内容
  if (renderedContentCache.has(cacheKey)) {
    // 这是一个后备机制，通常上面的useEffect中的缓存检查会先触发
    return (
      <Suspense fallback={<div>Loading JSON renderer...</div>}>
        {renderedContentCache.get(cacheKey)}
      </Suspense>
    );
  }
  
  // 如果仍在加载或没有内容，显示加载状态
  if (isLoading || !renderedContent) {
    return <div>Loading JSON renderer...</div>;
  }
  
  // 渲染缓存的内容
  return (
    <Suspense fallback={<div>Loading JSON renderer...</div>}>
      {renderedContent}
    </Suspense>
  );
});

/**
 * 复制按钮组件
 * @param {object} props - 组件属性
 * @returns {JSX.Element} 复制按钮组件
 */
const CopyButton = ({ content }) => {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        console.log('已复制到剪贴板');
        // 可以添加复制成功的视觉反馈
      })
      .catch(err => {
        console.error('复制失败:', err);
      });
  };
  
  return (
    <button 
      className={fileStyles.copyButton} 
      onClick={() => copyToClipboard(typeof content === 'object' 
        ? JSON.stringify(content, null, 2) 
        : String(content))}
      title="复制内容"
      style={{
        position: 'absolute',
        top: '5px',
        right: '5px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        color: '#9cdcfe'
      }}
    >
      📋
    </button>
  );
};

// 定义函数用于生成更加稳定和唯一的缓存键
const generateCacheKey = (fileName, content) => {
  try {
    // 提取文件名的基础部分，通常是路径的最后一部分
    const baseFileName = fileName.split('/').pop().split('\\').pop();
    
    // 对于对象类型的内容
    if (typeof content === 'object') {
      // 计算内容的哈希值 - 使用字符串的前100个字符和后100个字符
      const contentStr = JSON.stringify(content);
      const contentLength = contentStr.length;
      const prefix = contentStr.substring(0, Math.min(100, contentLength));
      const suffix = contentStr.substring(Math.max(0, contentLength - 100));
      
      // 根据文件名和内容的特征生成缓存键
      return `${baseFileName}_${contentLength}_${prefix.length + suffix.length}`;
    }
    
    // 对于字符串类型的内容
    if (typeof content === 'string') {
      const contentLength = content.length;
      const prefix = content.substring(0, Math.min(50, contentLength));
      const suffix = content.substring(Math.max(0, contentLength - 50));
      
      return `${baseFileName}_${contentLength}_${prefix.length + suffix.length}`;
    }
    
    // 默认键
    return `${baseFileName}_${typeof content}_${Date.now()}`;
  } catch (e) {
    console.error('Error generating cache key:', e);
    // 出错时使用时间戳作为后备方案
    return `${fileName}_${Date.now()}`;
  }
};

/**
 * 特别处理.final文件中的Markdown内容，采用与ExpertMessage相同的混杂内容处理策略
 * @param {string} fileName - 文件名
 * @param {string} content - Markdown内容
 * @param {object} options - 渲染选项
 * @returns {JSX.Element} 渲染结果
 */
export const renderFinalMarkdown = (fileName, content, options) => {
  // 处理空内容
  if (!content) {
    return <div className={fileStyles.emptyContent}>无内容</div>;
  }

  // 将内容分割成行
  const lines = content.split('\n');
  
  // 渲染内容函数
  const renderFinalContent = () => {
    // 用于跟踪已被处理（例如作为Mermaid块一部分）的行索引
    const processedIndices = new Set();
    const elements = []; // 存储要渲染的JSX元素

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      // 如果当前行已被处理，跳过
      if (processedIndices.has(lineIndex)) {
        continue;
      }
      
      const line = lines[lineIndex];
      const trimmedLine = line.trim();

      // 1. 检测所有类型的代码块（不仅仅是mermaid）
      if (trimmedLine.startsWith('```')) {
        let endIndex = -1;
        // 向前查找结束标记
        for (let j = lineIndex + 1; j < lines.length; j++) {
          if (lines[j].trim() === '```') {
            endIndex = j;
            break;
          }
        }

        if (endIndex !== -1) {
          // 找到了完整的代码块
          const codeBlockContent = lines.slice(lineIndex, endIndex + 1).join('\n');
          // 标记这个块的所有行为已处理
          for (let k = lineIndex; k <= endIndex; k++) {
            processedIndices.add(k);
          }
          
          // 提取语言类型
          const languageMatch = trimmedLine.match(/^```(\w+)/);
          const language = languageMatch ? languageMatch[1] : '';
          

          
          // 根据语言类型选择渲染方式
          if (language === 'mermaid') {
            // 方案三：生成基于内容的稳定key
            const mermaidContent = lines.slice(lineIndex + 1, endIndex).join('\n');
            let contentHash = 0;
            for (let i = 0; i < mermaidContent.length; i++) {
              const char = mermaidContent.charCodeAt(i);
              contentHash = ((contentHash << 5) - contentHash) + char;
              contentHash = contentHash & contentHash;
            }
            const stableKey = `mermaid-block-${lineIndex}-${Math.abs(contentHash)}`;
            
            // Mermaid图表使用特殊样式
            elements.push(
              <div key={stableKey} className={`${fileStyles.contentBlock} ${fileStyles.mermaidBlock}`}>
                <UnifiedContentRenderer 
                  content={codeBlockContent}
                  collapsible={false}
                  renderOptions={{
                    showDebugInfo: ENABLE_MERMAID_DEBUG,
                    mermaidOptions: {
                      theme: 'dark',
                      securityLevel: 'loose',
                      flowchart: { curve: 'basis' }
                    },
                    compactMode: true // 启用紧凑模式
                  }}
                />
              </div>
            );
          } else if (language === 'markdown') {
            // Markdown代码块：提取内容并作为Markdown渲染
            const markdownContent = lines.slice(lineIndex + 1, endIndex).join('\n');
            elements.push(
              <div key={`block-markdown-${lineIndex}`} className={`${fileStyles.contentBlock} ${fileStyles.markdownBlock}`}>
                <UnifiedContentRenderer 
                  content={markdownContent}
                  collapsible={false}
                  renderOptions={{
                    customClass: fileStyles.markdownContent,
                    tableClass: fileStyles.markdownTable,
                    showDebugInfo: false, // Markdown块不需要调试信息
                    compactMode: true // 启用紧凑模式
                  }}
                />
              </div>
            );
          } else {
            // 其他代码块：提取纯净内容并直接使用SyntaxHighlighter渲染
            const pureCodeContent = lines.slice(lineIndex + 1, endIndex).join('\n');
            elements.push(
              <div key={`block-code-${lineIndex}`} className={`${fileStyles.contentBlock} ${fileStyles.codeBlock}`}>
                <div className="code-block-wrapper">
                  <div className="code-block-header">
                    <span className="code-language">{language || 'text'}</span>
                    <button 
                      className="copy-code-button"
                      onClick={() => {
                        navigator.clipboard.writeText(pureCodeContent);
                      }}
                      title="复制代码"
                    >
                      复制
                    </button>
                  </div>
                  <SyntaxHighlighter
                    language={language || 'text'}
                    style={vscDarkPlus}
                    customStyle={{
                      margin: 0, 
                      borderRadius: '0 0 4px 4px',
                      padding: '8px'
                    }}
                    showLineNumbers={pureCodeContent.split('\n').length > 5}
                    wrapLines={true}
                  >
                    {pureCodeContent}
                  </SyntaxHighlighter>
                </div>
              </div>
            );
          }
          // 继续下一次外层循环
          continue; 
        } 
        // 如果没找到结束标记，则将 ``` 视为普通文本处理（会落入下面的文本块逻辑）
      }

      // 2. 处理空行 - 使用更小的空行样式
      if (trimmedLine === '') {
        processedIndices.add(lineIndex);
        elements.push(<div key={`empty-${lineIndex}`} className={fileStyles.emptyLine}>&nbsp;</div>);
        continue;
      }

      // 3. 处理普通文本块 (合并连续的普通文本行)
      let textBlockLines = [];
      let currentTextIndex = lineIndex;
      // 合并直到遇到特殊行类型或文件末尾
      while (
        currentTextIndex < lines.length &&
        !processedIndices.has(currentTextIndex) &&
        !lines[currentTextIndex].trim().startsWith('```') && // 停止于任何 ``` 标记
        lines[currentTextIndex].trim() !== ''
      ) {
        textBlockLines.push(lines[currentTextIndex]);
        processedIndices.add(currentTextIndex); // 标记为已处理
        currentTextIndex++;
      }

      // 如果找到了文本块，渲染它
      if (textBlockLines.length > 0) {
        const textBlockContent = textBlockLines.join('\n');
        // 判断是否是复杂块 (多于一行，或包含表格标记)
        const isComplex = textBlockLines.length > 1 || 
                          textBlockContent.includes('|'); // 主要检查表格
        
        elements.push(
          <div key={`block-text-${lineIndex}`} className={fileStyles.contentBlock}>
            <UnifiedContentRenderer 
              content={textBlockContent}
              collapsible={false}
              // 仅在多行或含表格时应用 finalMarkdownBlock 样式
              className={isComplex ? fileStyles.finalMarkdownBlock : ''}
              renderOptions={{
                customClass: fileStyles.markdownContent,
                tableClass: fileStyles.markdownTable,
                codeClass: fileStyles.codeBlock,
                nestingLevel: 0,
                showDebugInfo: false, // 文本块不需要调试信息
                compactMode: true // 启用紧凑模式
              }}
            />
          </div>
        );
        // 将 lineIndex 设置到这个块的末尾，以便下一次迭代从正确的位置开始
        lineIndex = currentTextIndex - 1; 
      } else {
        // 如果当前行无法归类（理论上不应发生），则标记为已处理以防无限循环
        if (!processedIndices.has(lineIndex)) { // 再次检查以防万一
           processedIndices.add(lineIndex);
        }
      }
    } // End of for loop

    return elements;
  };

  // 渲染最终内容
  return (
    <div className={fileStyles.finalMarkdownContainer}>
      {renderFinalContent()}
      {/* 删除复制按钮 */}
    </div>
  );
};